/**
 * /admin/roles — role catalog.
 *
 * Shows every role with:
 *  - A thematic icon (admin = shield, marketing = megaphone, etc.)
 *  - The first few users assigned (avatar stack + count)
 *  - A "View users with this role" link that deep-links to
 *    /admin/users?role=<name>
 *
 * The Administrator role is visually emphasised (gold gradient strip,
 * "All-access" wildcard badge) because it implies every other role's
 * access — see hy_sales.auth.dependencies.ADMIN_ROLE.
 *
 * System roles cannot be deleted (no backend endpoint exists). Custom
 * roles are deferred to a future iteration that adds Edit / Delete.
 */

import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ComponentType, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createRole, fetchRoles, fetchUsers, type RoleWithUsage } from '../../api/admin';

const INK = '#0f0a07';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

// Icon + brand colour per known role.  Anything unmapped falls back
// to a neutral Badge icon so newly-created custom roles still render.
interface RoleVisual {
  Icon: ComponentType<{ sx?: object }>;
  tint: string; // border / accent
  bg: string; // icon-disc background
  fg: string; // icon-disc foreground
}

const ROLE_VISUALS: Record<string, RoleVisual> = {
  admin: {
    Icon: AdminPanelSettingsIcon,
    tint: GOLD,
    bg: 'linear-gradient(135deg, #e9c46a 0%, #bb8c3f 55%, #8e6a2a 100%)',
    fg: INK,
  },
  depletions: {
    Icon: LocalBarOutlinedIcon,
    tint: '#a86b1e',
    bg: 'rgba(168, 107, 30, 0.10)',
    fg: '#a86b1e',
  },
  marketing: {
    Icon: CampaignOutlinedIcon,
    tint: '#22865a',
    bg: 'rgba(34, 134, 90, 0.10)',
    fg: '#22865a',
  },
};

const FALLBACK_VISUAL: RoleVisual = {
  Icon: BadgeOutlinedIcon,
  tint: 'rgba(15, 10, 7, 0.4)',
  bg: 'rgba(15, 10, 7, 0.06)',
  fg: 'rgba(15, 10, 7, 0.55)',
};

const visualFor = (roleName: string): RoleVisual => ROLE_VISUALS[roleName] ?? FALLBACK_VISUAL;

export function RolesPage() {
  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: fetchRoles,
  });
  const [addOpen, setAddOpen] = useState(false);

  const roles = rolesQuery.data?.items ?? [];

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
              boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
            },
          }}
        >
          Add Role
        </Button>
      </Box>

      {rolesQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      ) : rolesQuery.error ? (
        <Alert severity="error">{(rolesQuery.error as Error).message}</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} />
          ))}
        </Box>
      )}

      <AddRoleDialog open={addOpen} onClose={() => setAddOpen(false)} />
    </Stack>
  );
}

function RoleCard({ role }: { role: RoleWithUsage }) {
  const navigate = useNavigate();
  const isAdmin = role.name === 'admin';
  const visual = visualFor(role.name);

  // Avatar preview — fetch up to 4 users with this role for the stack.
  // Cached per role, so revisiting the page is instant.
  const previewQuery = useQuery({
    queryKey: ['admin', 'roles', 'preview', role.name],
    queryFn: () => fetchUsers({ role: role.name, limit: 4 }),
    enabled: role.user_count > 0,
  });
  const previewUsers = previewQuery.data?.items ?? [];
  const remaining = Math.max(0, role.user_count - previewUsers.length);

  const goToUsers = () => navigate(`/admin/users?role=${encodeURIComponent(role.name)}`);

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: IVORY_LIGHT,
        borderRadius: 2,
        border: '1px solid rgba(15, 10, 7, 0.08)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease',
        '&:hover': {
          borderColor: isAdmin ? GOLD : 'rgba(15, 10, 7, 0.16)',
          boxShadow: isAdmin
            ? '0 8px 28px rgba(187, 140, 63, 0.18)'
            : '0 4px 18px rgba(15, 10, 7, 0.06)',
        },
      }}
    >
      {/* Top accent strip — admin gets the full gold gradient.
          Everything else gets a subtle tonal strip in the role colour. */}
      <Box
        sx={{
          height: isAdmin ? 4 : 2,
          background: isAdmin
            ? `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, ${GOLD} 100%)`
            : visual.tint,
          opacity: isAdmin ? 1 : 0.6,
        }}
      />

      <Box sx={{ p: 2.25, display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Header row: icon disc + title block + system chip */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: visual.bg,
              color: visual.fg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: isAdmin ? 'inset 0 1px 0 rgba(255,255,255,0.35)' : 'none',
            }}
          >
            <visual.Icon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontWeight: 700,
                  fontSize: 15.5,
                  letterSpacing: '-0.01em',
                  color: INK,
                  lineHeight: 1.2,
                }}
              >
                {role.display_name}
              </Typography>
              {isAdmin && (
                <Tooltip
                  title="Implicitly grants access to every section, present and future."
                  arrow
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.35,
                      px: 0.7,
                      py: 0.2,
                      borderRadius: 0.6,
                      bgcolor: 'rgba(187, 140, 63, 0.16)',
                      color: '#8e6a2a',
                      fontFamily: FONT_INTER,
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                    }}
                  >
                    All-access
                  </Box>
                </Tooltip>
              )}
            </Box>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: 11,
                color: 'rgba(15, 10, 7, 0.45)',
                mt: 0.25,
              }}
            >
              {role.name}
            </Typography>
          </Box>
          {role.is_system && (
            <Tooltip title="Built-in role. Cannot be deleted." arrow>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.35,
                  px: 0.7,
                  py: 0.2,
                  borderRadius: 0.6,
                  border: `1px solid ${GOLD}`,
                  color: GOLD,
                  fontFamily: FONT_INTER,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                }}
              >
                <VerifiedIcon sx={{ fontSize: 11 }} />
                System
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Description */}
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 13,
            color: 'rgba(15, 10, 7, 0.65)',
            lineHeight: 1.5,
            minHeight: 38,
          }}
        >
          {role.description ?? <em>No description.</em>}
        </Typography>

        {/* Footer: avatar stack + View users button */}
        <Box
          sx={{
            mt: 'auto',
            pt: 1.75,
            borderTop: '1px dashed rgba(15, 10, 7, 0.10)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          {role.user_count === 0 ? (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.4)',
                letterSpacing: '0.04em',
              }}
            >
              No users assigned
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Avatar stack */}
              <Box sx={{ display: 'flex' }}>
                {previewQuery.isLoading
                  ? Array.from({ length: Math.min(role.user_count, 3) }).map((_, i) => (
                      <AvatarSkeleton key={i} index={i} />
                    ))
                  : previewUsers.map((u, i) => (
                      <Avatar
                        key={u.id}
                        index={i}
                        initial={((u.first_name || u.email)[0] ?? '?').toUpperCase()}
                        title={`${u.first_name} ${u.last_name} · ${u.email}`}
                      />
                    ))}
                {remaining > 0 && !previewQuery.isLoading && (
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      bgcolor: 'rgba(15, 10, 7, 0.08)',
                      color: 'rgba(15, 10, 7, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: FONT_INTER,
                      fontWeight: 700,
                      fontSize: 10.5,
                      ml: -0.75,
                      border: `2px solid ${IVORY_LIGHT}`,
                      flexShrink: 0,
                    }}
                  >
                    +{remaining}
                  </Box>
                )}
              </Box>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: 'rgba(15, 10, 7, 0.55)',
                  letterSpacing: '0.02em',
                }}
              >
                {role.user_count} {role.user_count === 1 ? 'user' : 'users'}
              </Typography>
            </Box>
          )}

          <Tooltip title={`Show users assigned to ${role.display_name}`} arrow>
            <Button
              size="small"
              onClick={goToUsers}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: GOLD,
                '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
              }}
            >
              View users
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

function Avatar({ initial, index, title }: { initial: string; index: number; title: string }) {
  return (
    <Tooltip title={title} arrow>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 11,
          ml: index === 0 ? 0 : -0.75,
          border: `2px solid ${IVORY_LIGHT}`,
          flexShrink: 0,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
        }}
      >
        {initial}
      </Box>
    </Tooltip>
  );
}

function AvatarSkeleton({ index }: { index: number }) {
  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        bgcolor: 'rgba(15, 10, 7, 0.06)',
        ml: index === 0 ? 0 : -0.75,
        border: `2px solid ${IVORY_LIGHT}`,
      }}
    />
  );
}

interface AddRoleDialogProps {
  open: boolean;
  onClose: () => void;
}

function AddRoleDialog({ open, onClose }: AddRoleDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      createRole({
        name,
        display_name: displayName,
        description: description || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
      onClose();
      setName('');
      setDisplayName('');
      setDescription('');
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: FONT_INTER, fontWeight: 700, color: INK }}>
        Add Role
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {mutation.error && <Alert severity="error">{(mutation.error as Error).message}</Alert>}
          <TextField
            label="Name (machine-readable)"
            placeholder="e.g. analyst"
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            fullWidth
            required
            autoFocus
            helperText="Lowercase, no spaces. Used in code + URL guards."
          />
          <TextField
            label="Display name"
            placeholder="e.g. Data Analyst"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            required
            helperText="Shown to admins in role pickers."
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="One line of context shown next to the checkbox."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={mutation.isPending || !name || !displayName}
          onClick={() => mutation.mutate()}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
              boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
            },
          }}
        >
          {mutation.isPending ? 'Creating…' : 'Add Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
